import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageUpload from '../components/ImageUpload';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { Camera, CheckCircle, AlertCircle } from 'lucide-react';

const CheckInOut = ({ type }) => {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [booking, setBooking] = useState(null);
  const [car, setCar] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [odometer, setOdometer] = useState('');
  const [fuelLevel, setFuelLevel] = useState('full');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const isCheckIn = type === 'checkin';

  useEffect(() => {
    fetchBookingDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const { bookingsAPI, carsAPI } = await import('../api');
      
      const bookingRes = await bookingsAPI.getById(bookingId);
      const bookingData = bookingRes.data;
      setBooking(bookingData);

      // Fetch car
      try {
        const carRes = await carsAPI.getById(bookingData.car_id);
        setCar(carRes.data);
      } catch (error) {
        console.warn('Car not found:', error);
        setCar({
          id: bookingData.car_id,
          name: bookingData.car_name || 'Auto',
        });
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la reserva',
        variant: 'destructive',
      });
      navigate('/mobilitier/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (photos.length < 4) {
      toast({
        title: 'Error',
        description: 'Se requieren mínimo 4 fotos (exterior 4 ángulos)',
        variant: 'destructive',
      });
      return;
    }

    if (!odometer || parseInt(odometer) <= 0) {
      toast({
        title: 'Error',
        description: 'El odómetro es obligatorio y debe ser mayor a 0',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    
    try {
      const api = await import('../api');
      
      const checkData = {
        photos: photos,
        odometer: parseInt(odometer),
        fuel_level: fuelLevel,
        notes: notes,
        timestamp: new Date().toISOString(),
        completed_by: user.id,
        ...(isCheckIn ? {} : { damage_reported: damageReported, damage_notes: damageNotes }),
      };

      // Update booking with check-in or check-out data
      const endpoint = isCheckIn ? 'checkin' : 'checkout';
      await api.default.put(`/bookings/${bookingId}/${endpoint}`, checkData);

      toast({
        title: '¡Éxito!',
        description: `${isCheckIn ? 'Check-in' : 'Check-out'} completado correctamente`,
      });

      navigate('/mobilitier/dashboard');
    } catch (error) {
      console.error(`Error in ${type}:`, error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || `Error al completar ${type}`,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!booking || !car) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Reserva no encontrada</h2>
          <Button onClick={() => navigate('/mobilitier/dashboard')} className="mt-4">
            Volver al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Validate booking status
  const canCheckIn = isCheckIn && (booking.status === 'confirmed' || booking.status === 'approved');
  const canCheckOut = !isCheckIn && booking.status === 'active' && booking.check_in;

  if ((isCheckIn && !canCheckIn) || (!isCheckIn && !canCheckOut)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="w-6 h-6" />
              No Disponible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              {isCheckIn 
                ? 'Esta reserva no está lista para check-in. Debe estar aprobada primero.'
                : 'Esta reserva no está lista para check-out. Debe tener check-in completado y estar activa.'
              }
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Status actual: <span className="font-semibold">{booking.status}</span>
            </p>
            <Button onClick={() => navigate('/mobilitier/dashboard')} variant="outline" className="w-full">
              Volver al Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Camera className="w-8 h-8" />
            {isCheckIn ? 'Check-In' : 'Check-Out'}
          </h1>
          <p className="text-gray-600 mt-2">
            {car.name} - Reserva #{booking.id.slice(-8)}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Fotos del Auto */}
            <Card>
              <CardHeader>
                <CardTitle>Fotos del Vehículo</CardTitle>
                <p className="text-sm text-gray-600">
                  Toma fotos de 4 ángulos: Frente, Atrás, Lateral Izquierdo, Lateral Derecho
                  {!isCheckIn && booking.check_in && (
                    <span className="block mt-1 text-blue-600">
                      Compara con las fotos de check-in para documentar cualquier daño nuevo
                    </span>
                  )}
                </p>
              </CardHeader>
              <CardContent>
                <ImageUpload
                  maxFiles={8}
                  onUploadComplete={setPhotos}
                  folder={isCheckIn ? 'checkin' : 'checkout'}
                  existingImages={photos}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Fotos actuales: {photos.length}/8 (mínimo 4 requeridas)
                </p>
              </CardContent>
            </Card>

            {/* Detalles del Vehículo */}
            <Card>
              <CardHeader>
                <CardTitle>Estado del Vehículo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="odometer">Odómetro (km) *</Label>
                    <Input
                      id="odometer"
                      type="number"
                      value={odometer}
                      onChange={(e) => setOdometer(e.target.value)}
                      placeholder="12345"
                      min="0"
                      required
                    />
                    {!isCheckIn && booking.check_in?.odometer && (
                      <p className="text-xs text-gray-500 mt-1">
                        Check-in: {booking.check_in.odometer} km
                        {odometer && ` (${parseInt(odometer) - booking.check_in.odometer} km recorridos)`}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="fuel">Nivel de Combustible *</Label>
                    <Select value={fuelLevel} onValueChange={setFuelLevel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">Lleno (Full)</SelectItem>
                        <SelectItem value="3/4">3/4</SelectItem>
                        <SelectItem value="1/2">1/2</SelectItem>
                        <SelectItem value="1/4">1/4</SelectItem>
                        <SelectItem value="empty">Vacío</SelectItem>
                      </SelectContent>
                    </Select>
                    {!isCheckIn && booking.check_in?.fuel_level && (
                      <p className="text-xs text-gray-500 mt-1">
                        Check-in: {booking.check_in.fuel_level}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notas / Observaciones</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={isCheckIn 
                      ? "Describe el estado general del auto, rayones, abolladuras, etc."
                      : "Describe cualquier daño nuevo o problema encontrado durante el viaje"
                    }
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Damage report (Check-out only) — Neutral marketplace */}
            {!isCheckIn && (
              <Card className={damageReported ? 'border-amber-500 bg-amber-50' : ''}>
                <CardHeader>
                  <CardTitle className="text-base">¿Hay un daño nuevo en el vehículo?</CardTitle>
                  <p className="text-xs text-ink-500 font-light">
                    Mobility es un marketplace neutral. Si reportas un daño, el depósito quedará
                    retenido hasta 7 días (Stripe lo libera automáticamente). Mobility NO arbitra:
                    deberás resolver el desacuerdo directamente con el inquilino.
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={!damageReported ? 'default' : 'outline'}
                      onClick={() => setDamageReported(false)}
                      data-testid="checkout-no-damage-btn"
                      className={!damageReported ? 'bg-lime text-ink hover:bg-lime-500' : ''}
                    >
                      Sin daños — liberar depósito
                    </Button>
                    <Button
                      type="button"
                      variant={damageReported ? 'default' : 'outline'}
                      onClick={() => setDamageReported(true)}
                      data-testid="checkout-damage-btn"
                      className={damageReported ? 'bg-amber-600 text-white hover:bg-amber-700' : ''}
                    >
                      Reportar daño
                    </Button>
                  </div>
                  {damageReported && (
                    <Textarea
                      value={damageNotes}
                      onChange={(e) => setDamageNotes(e.target.value)}
                      placeholder="Describe el daño en detalle (rayón, golpe, faltante, etc.)"
                      rows={3}
                      data-testid="checkout-damage-notes"
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Resumen */}
            <Card className="bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900">
                      {isCheckIn ? 'Completar Check-In' : 'Completar Check-Out'}
                    </h4>
                    <p className="text-sm text-blue-800 mt-1">
                      {isCheckIn 
                        ? 'Al confirmar, el viaje comenzará y el usuario podrá usar el vehículo.'
                        : 'Al confirmar, el viaje finalizará y se liberará el depósito de seguridad.'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={submitting}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting || photos.length < 4 || !odometer}
                className="flex-1 bg-[#000000] hover:bg-ink-700"
              >
                {submitting ? 'Procesando...' : `Completar ${isCheckIn ? 'Check-In' : 'Check-Out'}`}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckInOut;
